import React from 'react';
import { Box, List } from '@mui/material';
import { StyledListItem, StyledLink, SubMenuContainer } from './MuiSidebarMenu.styles';
import { useCollapsedMenu } from '../../hooks/useCollapsedMenu';
import { useMenuItem } from '../../hooks/useMenuItem';
import CollapsedTooltip from './CollapsedTooltip';
import MenuContent from './MenuContent';
import { MenuItem as MenuItemType } from '../../types';

interface MenuItemProps {
    item: MenuItemType;
    level?: number;
    onItemClick?: (item: MenuItemType) => void;
    currentPath: string;
    expandedItems: string[];
    onToggleExpand: (itemKey: string, level?: number) => void;
    parentKey?: string;
    isCollapsed: boolean;
}

const MenuItem = React.memo(({ 
    item, 
    level = 0, 
    onItemClick, 
    currentPath, 
    expandedItems,
    onToggleExpand,
    parentKey,
    isCollapsed
}: MenuItemProps) => {
    const collapsedMenu = useCollapsedMenu(isCollapsed);
    const { hasActiveChild, getItemKey, handleClick } = useMenuItem();
    
    const hasChildren = Boolean(item.subMenu?.length);
    const isActive = currentPath === item.path || item.activePaths?.includes(currentPath);
    const itemKey = getItemKey(item, parentKey);
    const isOpen = expandedItems.includes(itemKey);
    const hasActiveChildInside = hasChildren && hasActiveChild(item.subMenu!, currentPath);

    if (item.component) {
        const Component = item.component;
        return (
            <StyledListItem level={level}>
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', py: 1.25 }}>
                    <Component {...(item.componentProps || {})} />
                </Box>
            </StyledListItem>
        );
    }

    const isMenuItem = hasChildren || !item.path;
    const className = `${isActive ? 'active' : ''} ${hasActiveChildInside ? 'has-active-child' : ''} ${isMenuItem ? 'menu-item' : ''}`.trim();
    const collapsedStyle = isCollapsed && level === 0 ? { px: 0, justifyContent: 'center' } : {};
    const content = <MenuContent item={item} isCollapsed={isCollapsed} level={level} />;
    
    const renderContent = () => {
        const wrapperStyle = { display: 'flex', width: '100%', alignItems: 'center', ...(isCollapsed && level === 0 ? { justifyContent: 'center' } : {}) };
        
        return !hasChildren && item.path ? (
            <StyledLink to={item.path} style={wrapperStyle}>{content}</StyledLink>
        ) : (
            <Box sx={wrapperStyle}>{content}</Box>
        );
    };

    return (
        <>
            <StyledListItem 
                level={level}
                className={className}
                onClick={(e) => handleClick(e, item, hasChildren, isCollapsed, level, onToggleExpand, itemKey, onItemClick)}
                onMouseEnter={(e) => collapsedMenu.handleMouseEnter(e, hasChildren, level)}
                onMouseLeave={() => collapsedMenu.handleMouseLeave(level)}
                sx={collapsedStyle}
            >
                {renderContent()}
            </StyledListItem>
            
            {hasChildren && !isCollapsed && (
                <SubMenuContainer in={isOpen}>
                    <List component="div" disablePadding>
                        {item.subMenu!.map((subItem, index) => (
                            <MenuItem
                                key={subItem.id || subItem.label || index}
                                item={subItem}
                                level={level + 1}
                                onItemClick={onItemClick}
                                currentPath={currentPath}
                                expandedItems={expandedItems}
                                onToggleExpand={onToggleExpand}
                                parentKey={itemKey}
                                isCollapsed={isCollapsed}
                            />
                        ))}
                    </List>
                </SubMenuContainer>
            )}

            {isCollapsed && level === 0 && hasChildren && (
                <CollapsedTooltip
                    showPopper={collapsedMenu.showPopper}
                    popperAnchor={collapsedMenu.popperAnchor}
                    item={item}
                    currentPath={currentPath}
                    hoveredSubItem={collapsedMenu.hoveredSubItem}
                    showSubMenu={collapsedMenu.showSubMenu}
                    subMenuAnchor={collapsedMenu.subMenuAnchor}
                    onPopperMouseEnter={collapsedMenu.handlePopperMouseEnter}
                    onPopperMouseLeave={collapsedMenu.handlePopperMouseLeave}
                    onSubItemHover={collapsedMenu.handleSubItemHover}
                    onSubItemLeave={collapsedMenu.handleSubItemLeave}
                    onSubMenuMouseEnter={collapsedMenu.handleSubMenuMouseEnter}
                    onSubMenuMouseLeave={collapsedMenu.handleSubMenuMouseLeave}
                    onPopperClickAway={collapsedMenu.closeMenus}
                    onItemClick={onItemClick}
                    onCloseMenus={collapsedMenu.closeMenus}
                />
            )}
        </>
    );
});

export default MenuItem;
